import { truncate } from "lodash-es";
import moment from "moment-timezone";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import LinkButton from "src/components/buttons/LinkButton";
import { ImageModal } from "src/components/modals/ImageModal";
import ClassModel from "src/models/ClassModel";
import MembershipOfferingModel from "src/models/MembershipOfferingModel";
import { useStore } from "src/stores/Store";
import calculateNumberOfClassesFromDate from "src/utils/calculateNumberOfClassesFromDate";
import calculateProratedRegistrationPrice from "src/utils/calculateProratedRegistrationPrice";
import { dateFormat } from "src/utils/constants";
import formatMoney from "src/utils/formatMoney";
import { modals } from "src/utils/modals";
import {
  AnnualFeeType,
  Frequency,
  OfferingStatus,
  RateType,
} from "types/code-generator";

interface Props {
  classItem: ClassModel;
  isDetail?: boolean;
  onExpand?: () => void;
}

export default function ClassDetail(props: Props) {
  const [viewingProgramDescription, setViewingProgramDescription] =
    useState(false);
  const [viewingOfferingDescription, setViewingOfferingDescription] =
    useState(false);

  const store = useStore();
  const location = useLocation();

  function viewProgramDescription() {
    setViewingProgramDescription((current) => !current);

    if (props.onExpand) {
      props.onExpand();
    }
  }

  function viewOfferingDescription() {
    setViewingOfferingDescription((current) => !current);

    if (props.onExpand) {
      props.onExpand();
    }
  }

  function renderPricingDetails() {
    let proratedPrice = props.classItem.price;
    const chargeAmounts = {
      default: [],
    };

    for (const ca of props.classItem.chargeAmounts) {
      if (ca.membershipOffering) {
        if (!chargeAmounts[ca.membershipOffering.title]) {
          chargeAmounts[ca.membershipOffering.title] = [];
        }

        chargeAmounts[ca.membershipOffering.title].push(ca);
      } else {
        chargeAmounts["default"].push(ca);
      }
    }

    if (props.classItem.paymentRate === RateType.Variable) {
      const allDates = [];
      const startDate = moment(props.classItem.startDate);
      const endDate = moment(
        props.classItem.endDate
          ? props.classItem.endDate
          : startDate.clone().add(6, "months")
      );

      let date = startDate.clone().startOf("month");

      console.log("date", date.format());

      while (date.isBefore(endDate, "day")) {
        allDates.push(date);
        date = date.clone().add(1, "month");
      }

      return (
        <div>
          <h3 className="step-subhead">Pricing Details & Charge Schedule</h3>
          {allDates.map((d, i) => {
            let summaryEndDate = d.clone().endOf("month");

            if (summaryEndDate.isAfter(endDate)) {
              summaryEndDate = endDate.clone();
            }

            const number = calculateNumberOfClassesFromDate({
              startDate: d,
              endDate: summaryEndDate,
              classItem: props.classItem,
              organization: store.organization,
            });
            const totalPrice = props.classItem.price * number;

            return (
              <div key={i} className="mb-2">
                <div>
                  <strong>{d.format(dateFormat)}</strong>
                </div>
                <div>
                  ${formatMoney(totalPrice)} • {number}{" "}
                  {props.classItem.type.toLowerCase()}
                  {props.classItem.type === "Class" && number !== 1
                    ? "es"
                    : "s"}{" "}
                  at ${formatMoney(props.classItem.price)} each
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (props.classItem.chargeAmounts.length > 0) {
      return (
        <div>
          {Object.keys(chargeAmounts)
            .sort((a, b) => a.localeCompare(b))
            .map((k, i) => {
              return (
                <div key={i} className="mb-6">
                  {k !== "default" && (
                    <div className="step-subhead mb-2">
                      {k} Charge Details & Schedule
                    </div>
                  )}
                  {k === "default" && (
                    <div className="step-subhead mb-2">
                      Base Price Charge Details & Schedule
                    </div>
                  )}

                  {chargeAmounts[k]
                    .sort((a, b) => {
                      if (!a.date && b.date) {
                        return -1;
                      }

                      if (!b.date && a.date) {
                        return 1;
                      }

                      return (
                        new Date(a.date).getTime() - new Date(b.date).getTime()
                      );
                    })
                    .map((c, ii) => {
                      return (
                        <div key={ii} className="mb-2">
                          {c.atRegistration && (
                            <div>At the time of registration</div>
                          )}
                          {!c.atRegistration && (
                            <div>{moment(c.date).format(dateFormat)}</div>
                          )}
                          <strong>${formatMoney(c.price)}</strong>
                        </div>
                      );
                    })}
                </div>
              );
            })}
        </div>
      );
    }

    if (
      props.classItem.prorateInProgressClasses ||
      props.classItem.prorateExcludedDates
    ) {
      proratedPrice = calculateProratedRegistrationPrice({
        classItem: props.classItem,
        endDate: moment().endOf("month").toDate(),
        startDate: new Date(),
        organization: store.organization,
      });
    }

    let proratedPriceInfo;
    let dueInfo;

    const frequency =
      props.classItem.paymentFrequency === Frequency.Monthly
        ? "per month"
        : props.classItem.paymentFrequency === Frequency.Annual
          ? "per year"
          : "one-time";

    if (
      props.classItem.status !== OfferingStatus.Archived &&
      props.classItem.price &&
      proratedPrice !== props.classItem.price
    ) {
      proratedPriceInfo = (
        <span className="pl-1 text-empty">
          ${formatMoney(proratedPrice)} prorated
        </span>
      );
    }

    if (
      props.classItem.status === OfferingStatus.InProgress &&
      props.classItem.price > 0
    ) {
      dueInfo = <span className="text-empty">due today</span>;
    }

    let price = (
      <div>
        <strong>
          ${formatMoney(props.classItem.price || 0)} {frequency}
        </strong>{" "}
        {proratedPriceInfo} {dueInfo}
      </div>
    );

    if (
      (!props.classItem.price || props.classItem.price === 0) &&
      props.classItem.freePresentation !== null
    ) {
      if (props.classItem.freePresentation === "blank") {
        price = null;
      } else {
        price = (
          <div>
            <strong>{props.classItem.freePresentation}</strong>
          </div>
        );
      }
    }

    return (
      price && (
        <div className="mb-4">
          <h3 className="step-subhead">Pricing Details</h3>
          {price}
        </div>
      )
    );
  }

  function renderChargesCredits() {
    if (
      props.classItem.charges.length === 0 &&
      props.classItem.credits.length === 0
    ) {
      return null;
    }

    return (
      <div className="mb-4">
        <h3 className="step-subhead">
          One-time registration charges & credits
        </h3>
        {props.classItem.charges.map((c) => {
          return (
            <div key={c.id}>
              ${formatMoney(c.amount)}
              {c.billingCategory && ` • ${c.billingCategory.title}`}
            </div>
          );
        })}
        {props.classItem.credits.map((c) => {
          return (
            <div key={c.id}>
              <span className="text-success">-${formatMoney(c.amount)}</span>
              {c.billingCategory && ` • ${c.billingCategory.title}`}
            </div>
          );
        })}
      </div>
    );
  }

  function renderEligibility() {
    if (props.classItem.allowNonMembers) {
      return null;
    }

    return (
      <div className="mb-4">
        <h3 className="step-subhead">Eligibility</h3>
        <div className="mb-2 text-sm text-empty">
          This {props.classItem.type.toLowerCase()} is available to accounts
          with the following memberships.{" "}
          {!store.user && (
            <>
              If you are a member,{" "}
              <LinkButton to={`/login?returnPathname=${location.pathname}`}>
                sign in
              </LinkButton>{" "}
              to register.
            </>
          )}
        </div>
        <div className="divide-y divide-borderGray">
          {[...props.classItem.membershipOfferings].map((m) => {
            const published = m.publishDate && moment(m.publishDate);

            if (!published || today.isBefore(published)) {
              return (
                <div key={m.id}>
                  <div
                    css={{
                      color: store.theme.orgColor,
                    }}
                    className="flex items-center py-2"
                  >
                    <i
                      className="far fa-certificate mr-1"
                      css={{
                        color: store.theme.orgColor,
                      }}
                    />
                    <div className="flex-1">
                      {m.program && m.program.title}{" "}
                      {m.program && m.title && ` • `} {m.title}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div key={m.id}>
                <LinkButton
                  className="flex py-2 text-organization"
                  to={`/find/${m.id}`}
                >
                  <i className="far fa-certificate mr-1 mt-1 text-organization" />
                  <div className="flex-1">
                    {m.program && m.program.title}{" "}
                    {m.program && m.title && ` • `} {m.title}
                  </div>
                </LinkButton>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderMemberPricing() {
    if (
      props.classItem.membershipPrices.length === 0 ||
      props.classItem.membershipPrices.every((mp) => mp.price === 0)
    ) {
      return null;
    }

    let frequency =
      props.classItem.paymentFrequency === Frequency.Monthly
        ? "per month"
        : props.classItem.paymentFrequency === Frequency.Annual
          ? "per year"
          : "one-time";

    if (props.classItem.paymentRate === RateType.Variable) {
      frequency = `per ${props.classItem.type.toLowerCase()} time`;
    }

    const prorate =
      props.classItem.prorateInProgressClasses ||
      props.classItem.prorateExcludedDates;
    let proratedPrice;
    let proratedPriceInfo;

    if (prorate) {
      proratedPrice = calculateProratedRegistrationPrice({
        classItem: props.classItem,
        endDate: moment().endOf("month").toDate(),
        startDate: new Date(),
        organization: store.organization,
      });
    }

    if (
      props.classItem.status !== OfferingStatus.Archived &&
      props.classItem.price &&
      proratedPrice &&
      proratedPrice !== props.classItem.price &&
      moment(props.classItem.startDate).isBefore(today)
    ) {
      proratedPriceInfo = (
        <span className="pl-1 text-empty">
          ${formatMoney(proratedPrice)} prorated
        </span>
      );
    }

    return (
      <div className="mb-4">
        <h3 className="step-subhead">Member Pricing</h3>
        {props.classItem.price && (
          <div className="mb-2">
            Base Price
            <div>
              <strong>
                ${formatMoney(props.classItem.price)} {frequency}
              </strong>{" "}
              {proratedPriceInfo}
            </div>
          </div>
        )}
        {props.classItem.membershipPrices.map((mp) => {
          let proratedPrice;
          let proratedPriceInfo;
          let membershipOffering = props.classItem.membershipOfferings.find(
            (m) => m.id === mp.membershipOfferingId
          );

          if (!membershipOffering && mp.membershipOffering) {
            membershipOffering = new MembershipOfferingModel(
              mp.membershipOffering
            );
          }

          if (prorate) {
            proratedPrice = calculateProratedRegistrationPrice({
              classItem: new ClassModel({
                ...props.classItem,
                price: mp.price,
              }),
              endDate: moment().endOf("month").toDate(),
              startDate: new Date(),
              organization: store.organization,
            });
          }

          if (
            props.classItem.status !== OfferingStatus.Archived &&
            props.classItem.price &&
            proratedPrice &&
            proratedPrice !== props.classItem.price &&
            moment(props.classItem.startDate).isBefore(today)
          ) {
            proratedPriceInfo = (
              <span className="pl-1 text-empty">
                ${formatMoney(proratedPrice)} prorated
              </span>
            );
          }

          return (
            <div key={mp.id} className="mb-2">
              {membershipOffering?.program?.title || membershipOffering?.title}
              <div>
                <strong>
                  ${formatMoney(mp.price)} {frequency}
                </strong>{" "}
                {proratedPriceInfo}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function viewImage(image) {
    let newImages = props.classItem.program?.images.map((i) => i.url);
    const index = props.classItem.program?.images.findIndex(
      (i) => i.id === image.id
    );

    console.log("index", index);

    if (index > 0) {
      newImages = [
        newImages[index],
        ...newImages.slice(index + 1),
        ...newImages.slice(0, index),
      ];
    }

    modals.show(ImageModal, {
      images: newImages,
      index,
    });
  }

  let programDescription;
  let classDescription;
  let annualFee;
  let additionalFee;
  let excludedDates = [];
  let excludedDatesDescription;

  const today = moment();

  if (props.classItem.excludedDates.length > 0) {
    excludedDates = excludedDates.concat(props.classItem.excludedDates);
  }

  if (excludedDates.length > 0) {
    excludedDates.sort((a, b) => {
      return moment(a).valueOf() - moment(b).valueOf();
    });

    const excludedDatesItems = excludedDates
      .filter((d) => {
        const excludedDate = moment(d);

        return (
          excludedDate.isAfter(today) &&
          excludedDate.diff(today, "days") <= 365 &&
          excludedDate.isAfter(moment(props.classItem.startDate), "day") &&
          ((props.classItem.endDate &&
            excludedDate.isSameOrBefore(
              moment(props.classItem.endDate),
              "day"
            )) ||
            !props.classItem.endDate) &&
          props.classItem.classTimes.find(
            (ct) => ct.day === excludedDate.format("dddd")
          )
        );
      })
      .map((d) => moment(d).format(dateFormat));

    if (excludedDatesItems.length > 0) {
      excludedDatesDescription = (
        <>
          <h3 className="step-subhead">{props.classItem.type} Will Not Meet</h3>
          <p>{excludedDatesItems.join("; ")}</p>
        </>
      );
    }
  }

  if (
    props.classItem.program?.description?.length > 100 &&
    !viewingProgramDescription &&
    !props.isDetail
  ) {
    programDescription = (
      <p>
        {truncate(props.classItem.program?.description, {
          length: 100,
          omission: "…",
        })}{" "}
        <LinkButton onClick={viewProgramDescription}>expand</LinkButton>
      </p>
    );
  } else if (
    props.classItem.program?.description &&
    props.classItem.program?.description !== "<p></p>"
  ) {
    programDescription = (
      <p>
        {props.classItem.program.description.split("\n").map((item, key) => {
          return (
            <span key={key}>
              {item}
              <br />
            </span>
          );
        })}
        {props.classItem.program.description.length > 100 && !props.isDetail ? (
          <LinkButton onClick={viewProgramDescription}>collapse</LinkButton>
        ) : null}
      </p>
    );
  }

  if (
    props.classItem.description &&
    props.classItem.description.length > 100 &&
    !viewingOfferingDescription &&
    !props.isDetail
  ) {
    classDescription = (
      <p>
        {truncate(props.classItem.description, { length: 100, omission: "…" })}{" "}
        <LinkButton onClick={viewOfferingDescription}>expand</LinkButton>
      </p>
    );
  } else if (
    props.classItem.description &&
    props.classItem.description !== "<p><br></p>"
  ) {
    classDescription = (
      <div>
        <div
          dangerouslySetInnerHTML={{ __html: props.classItem.description }}
        />
        {props.classItem.description.length > 100 && !props.isDetail ? (
          <LinkButton onClick={viewOfferingDescription}>collapse</LinkButton>
        ) : null}
      </div>
    );
  }

  if (
    props.classItem.department &&
    props.classItem.department.annualFeeAmount > 0 &&
    props.classItem.applyAnnualFee
  ) {
    annualFee = (
      <>
        <h3 className="step-subhead">
          Annual Fee for {store.organization.title}
        </h3>
        <div className="mb-4">
          <strong>
            ${formatMoney(props.classItem.department.annualFeeAmount)}
          </strong>{" "}
          {props.classItem.department.annualFeeBillingCategory.title}{" "}
          <span className="text-sm text-empty">
            {props.classItem.department.annualFeeType === AnnualFeeType.Account
              ? "per family"
              : "per student"}
          </span>
        </div>
      </>
    );
  }

  if (props.classItem.additionalFeeAmount) {
    additionalFee = (
      <>
        <h3 className="step-subhead">One-Time Additional Fee</h3>
        <div className="mb-4">
          <strong>${formatMoney(props.classItem.additionalFeeAmount)}</strong>{" "}
          {props.classItem.additionalFeeTitle}
        </div>
      </>
    );
  }

  const pricingDetails = renderPricingDetails();
  const chargesCredits = renderChargesCredits();
  const eligibility = renderEligibility();
  const memberPricing = renderMemberPricing();

  return (
    <>
      {props.isDetail && <hr />}

      {props.isDetail && props.classItem.program?.images?.length > 0 && (
        <div className="-mx-4 mb-4 mt-6 overflow-x-auto whitespace-nowrap px-4">
          {props.classItem.program?.images.map((i) => (
            <button
              type="button"
              key={i.id}
              className="relative mr-6 inline-block cursor-pointer rounded-md"
              onClick={() => viewImage(i)}
            >
              <img
                className="h-40 rounded-md object-cover"
                src={i.url}
                alt="Image"
              />
            </button>
          ))}
        </div>
      )}

      <div>
        {programDescription && (
          <>
            <h3 className="step-subhead">Program Description</h3>
            <div
              className="mb-4"
              dangerouslySetInnerHTML={{
                __html: props.classItem.program?.description?.replace(
                  /<p><br><\/p>/g,
                  ""
                ),
              }}
            />
          </>
        )}

        {classDescription && (
          <>
            <h3 className="step-subhead">{props.classItem.type} Description</h3>
            <div
              className="mb-4 max-w-prose"
              css={{
                ["& p"]: {
                  margin: "0 0 1rem 0",
                },
                ["& a"]: {
                  textDecoration: "underline",
                },
                ["& ul"]: {
                  listStyleType: "disc",
                  padding: "0 0 0 2.5rem",
                },
              }}
              dangerouslySetInnerHTML={{
                __html: props.classItem.description,
              }}
            />
          </>
        )}
        {eligibility}
        {memberPricing}
        {!memberPricing && pricingDetails}
        {chargesCredits}
        {annualFee}
        {additionalFee}
        {excludedDatesDescription}
      </div>
    </>
  );
}
